import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import React from "react";

import { useSmartPrescriptionCoordinator } from "./use-smart-prescription-coordinator";

type Props = {
  /**
   * Edit mode. The recipe is already filled and saved from creation, so the
   * transcript that exists on mount (the already-recorded audio) must NOT
   * re-fill / overwrite the doctor's recipe — it only surfaces the saved
   * recommendations. Only *new* audio (a later transcript) supplements the
   * recipe fully. In create mode (default) every ready transcript fills.
   */
  deferInitialFill?: boolean;
  /**
   * Override the "smart filling active" check. On edit the flag lives on the
   * patient, not only in the freshly-selected additional-service tags.
   */
  enabledOverride?: boolean;
  /** Start recording more audio after an error. */
  onRecordAudio?: () => void | Promise<void>;
  /** Retained for callers that still provide the removed result-modal actions. */
  onSaveDraft?: () => void | Promise<void>;
  onSubmit?: () => void | Promise<void>;
};

/**
 * Watches the audio-note transcript and, whenever a new ready transcript
 * appears, runs it through the model (grok) and pre-fills the recipe form.
 * While processing, the recipe is locked (see `isProcessing` in the slice);
 * on completion it is filled and unlocked. Adding more audio produces a new
 * transcript, which re-runs the whole chain.
 */
export function SmartPrescriptionCoordinator({
  deferInitialFill = false,
  enabledOverride,
  onRecordAudio,
}: Props = {}) {
  const {
    enabled,
    hardBusy,
    processing,
    operation,
    procStep,
    transcribing,
    uploadError,
    micError,
    errorText,
    showModal,
    handleDismiss,
    runAction,
    resumeGenerateWaiting,
  } = useSmartPrescriptionCoordinator({ deferInitialFill, enabledOverride });

  if (!enabled) {
    return null;
  }

  return (
    <Dialog.Root
      open={showModal}
      // Closing while busy (upload/model) is blocked; otherwise close == "continue manually".
      onOpenChange={(open) => {
        if (!open && !hardBusy) {
          handleDismiss();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="tw-fixed tw-inset-0 tw-z-[100] tw-bg-black/30 tw-backdrop-blur-sm" />
        <Dialog.Content
          className={clsx(
            "srx-modal",
            "tw-fixed tw-left-1/2 tw-top-1/2 tw-z-[100] -tw-translate-x-1/2 -tw-translate-y-1/2",
            "tw-max-h-[88vh] tw-w-[min(94%,620px)] tw-overflow-y-auto tw-overflow-x-hidden",
            "tw-rounded-2xl tw-border tw-border-solid tw-border-[#d2deed] tw-bg-white tw-p-8 tw-shadow-xl",
          )}
          onEscapeKeyDown={(event) => hardBusy && event.preventDefault()}
          onPointerDownOutside={(event) => hardBusy && event.preventDefault()}
          onInteractOutside={(event) => hardBusy && event.preventDefault()}
        >
          {processing ? (
            <SmartPrescriptionProcessingView
              procStep={procStep}
              operation={operation}
              canSkip={transcribing && !hardBusy}
              onSkip={handleDismiss}
            />
          ) : errorText ? (
            <SmartPrescriptionErrorView
              title={
                micError
                  ? "Нет доступа к микрофону"
                  : uploadError
                    ? "Не удалось загрузить аудио"
                    : "Не получилось обработать аудио"
              }
              message={errorText}
              onManual={handleDismiss}
              onRetry={onRecordAudio ? () => runAction(onRecordAudio) : undefined}
              onWait={resumeGenerateWaiting ? () => void resumeGenerateWaiting() : undefined}
            />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const PROC_STEPS = [
  { title: ["Шаг 1:", "Загружаем голосовой рецепт"], icon: <IconMic /> },
  { title: ["Шаг 2:", "Обрабатываем запись"], icon: <IconDoc /> },
  { title: ["Шаг 3:", "Анализируем содержание"], icon: <IconSearch /> },
];

const UPDATE_STEPS = [
  { title: ["Шаг 1:", "Удаляем запись"], icon: <IconMic /> },
  { title: ["Шаг 2:", "Обновляем рецепт"], icon: <IconDoc /> },
  { title: ["Шаг 3:", "Проверяем изменения"], icon: <IconSearch /> },
];

/** Processing view: hero + 3-step progress + "не покидайте страницу". */
function SmartPrescriptionProcessingView({
  procStep,
  operation,
  canSkip,
  onSkip,
}: {
  procStep: number;
  operation: "audio" | "recipe-update";
  canSkip: boolean;
  onSkip: () => void;
}) {
  const steps = operation === "recipe-update" ? UPDATE_STEPS : PROC_STEPS;
  return (
    <div>
      <header className="srx-top">
        <div className="srx-hero" aria-hidden>
          <IconWave />
        </div>
        <Dialog.Title asChild>
          <h1 className="srx-headline">
            {operation === "recipe-update" ? "Обновляем рецепт" : "Идет обработка записи"}
          </h1>
        </Dialog.Title>
      </header>

      <div className="srx-steps">
        <div className="srx-progress-track" aria-hidden>
          <div
            className="srx-progress-fill"
            style={{ width: procStep === 0 ? "0%" : procStep === 1 ? "50%" : "100%" }}
          />
        </div>

        {steps.map((step, index) => (
          <section
            key={index}
            className={clsx(
              "srx-step",
              index < procStep && "is-completed",
              index === procStep && "is-active",
            )}
          >
            <div className="srx-step-number">
              <span>{index + 1}</span>
            </div>
            <div className="srx-step-icon" aria-hidden>
              {step.icon}
            </div>
            <h2 className="srx-step-title">
              {step.title[0]}
              <br />
              {step.title[1]}
            </h2>
            <div className="srx-dots" aria-hidden>
              <span />
              <span />
              <span />
            </div>
          </section>
        ))}
      </div>

      <Dialog.Description asChild>
        <div className="srx-notice">
          <div className="srx-notice-icon" aria-hidden>
            i
          </div>
          <div>
            Пожалуйста, не закрывайте и не обновляйте страницу до завершения обработки.
          </div>
        </div>
      </Dialog.Description>

      {canSkip ? (
        <button type="button" className="srx-skip" onClick={onSkip}>
          Продолжить вручную, не дожидаясь
        </button>
      ) : null}
    </div>
  );
}

/** Result view: success + percent + recommendation + field breakdown + action cards. */
function SmartPrescriptionErrorView({
  title,
  message,
  onManual,
  onRetry,
  onWait,
}: {
  title: string;
  message: string;
  onManual: () => void;
  onRetry?: () => void;
  onWait?: () => void;
}) {
  const actionCount = 1 + (onRetry ? 1 : 0) + (onWait ? 1 : 0);
  return (
    <div>
      <header className="srx-top">
        <div className="srx-hero srx-hero--error" aria-hidden>
          <IconWarn />
        </div>
        <Dialog.Title asChild>
          <h2 className="srx-result-line">{title}</h2>
        </Dialog.Title>
      </header>

      <Dialog.Description asChild>
        <p className="srx-summary srx-summary--error">{message}</p>
      </Dialog.Description>

      <h3 className="srx-next-title">Что дальше</h3>
      <div className="srx-actions" style={{ "--srx-cols": actionCount } as React.CSSProperties}>
        {onWait ? (
          <button type="button" className="srx-action srx-action--voice" onClick={onWait}>
            <span className="srx-action-icon" aria-hidden>
              <IconSearch />
            </span>
            <span className="srx-action-name">Проверить результат</span>
          </button>
        ) : null}
        {onRetry ? (
          <button type="button" className="srx-action srx-action--voice" onClick={onRetry}>
            <span className="srx-action-icon" aria-hidden>
              <IconMic />
            </span>
            <span className="srx-action-name">Записать заново</span>
          </button>
        ) : null}
        <button type="button" className="srx-action srx-action--manual" onClick={onManual}>
          <span className="srx-action-icon" aria-hidden>
            <IconPencil />
          </span>
          <span className="srx-action-name">Заполнить вручную</span>
        </button>
      </div>
    </div>
  );
}

/** Waveform and step icons used by the processing and error states. */
function IconWave() {
  return (
    <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round">
      <path d="M8 32v4M14 27v14M20 22v24M26 28v12M32 16v32M38 24v20M44 20v28M50 27v14M56 31v6" />
    </svg>
  );
}
function IconMic() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round">
      <rect x="17" y="7" width="14" height="24" rx="7" />
      <path d="M11 23v2a13 13 0 0 0 26 0v-2M24 38v6M17 44h14" />
    </svg>
  );
}
function IconDoc() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 7h20v34H14z" />
      <path d="M19 16h10M19 22h10M19 28h7" />
    </svg>
  );
}
function IconSearch() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round">
      <circle cx="21" cy="21" r="12" />
      <path d="M30 30l10 10" />
    </svg>
  );
}
function IconWarn() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M24 6l20 34H4z" />
      <path d="M24 20v10M24 35v.5" />
    </svg>
  );
}
function IconPencil() {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 38l4-11L34 7l7 7-20 20-11 4z" />
      <path d="M29 12l7 7M14 27l7 7" />
    </svg>
  );
}
